<template>
  <v-layout column>
    <v-card
      color="white"
      class="mb-1 elevation-1"
    >
      <v-layout column>
        <v-layout align-center>
          <v-card-title
            class="title py-0 px-2"
            style="height: 60px"
          >
            {{ title }}
          </v-card-title>

          <v-divider
            v-if="!disableAdding"
            class="ma-2"
            inset
            vertical
          />

          <v-btn
            v-if="!disableAdding"
            :to="addRoute ? addRoute : { path: `/${resource}/0` }"
            color="primary"
            dark
            small
            class="my-3 mx-2"
          >
            Добавить
          </v-btn>

          <DataSort
            :store="store"
            :columns="visibleColumns"
          />

          <DataFilter
            :store="store"
            :disabled="loading"
          />

          <slot name="toolbar" />

          <FilterView
            :store="store"
            :disabled="loading"
          />

          <v-spacer />

          <DownloadXlsxBtn
            :loading="loadingExcel"
            :disabled="disabledDownload"
            class="my-3"
            @download="downloadOutputForm()"
          />

          <DataColumnsSettings
            :columns="columnsSettings"
            :resource="store"
          />
        </v-layout>

        <v-layout
          align-self-start
          class="scrollable-block"
        >
          <SwitchFilter
            :store="store"
            :disabled="loading"
          />
        </v-layout>
      </v-layout>
    </v-card>

    <v-data-table
      :items="items"
      :headers="headers"
      :loading="loading"
      :total-items="totalItems"
      :pagination.sync="pagination"
      :rows-per-page-items="[rowsCount]"
      hide-actions
      class="elevation-1 mb-1 table-striped"
      no-data-text="Нет данных"
    >
      <template
        slot="headers"
        slot-scope="props"
      >
        <tr class="text-xs-left px-3">
          <th
            v-for="header in props.headers"
            :key="header.name"
            :style="{
              minWidth: header.width ? `${header.width}px` : 'auto',
              whiteSpace: header.width ? 'normal' : 'nowrap',
            }"
          >
            <v-tooltip
              v-if="header.tooltip"
              top
            >
              <template v-slot:activator="{ on }">
                <span
                  v-if="header.tooltip === 'SyncedWithErp'"
                  style="cursor: pointer"
                  v-on="on"
                >
                  <v-icon>sync</v-icon>
                </span>
                <span v-else>{{ header.text }}</span>
              </template>
              <span>{{ header.text }}</span>
            </v-tooltip>
            <span v-else-if="header.tooltip === 'SyncedWithErp'">
              <v-icon>sync</v-icon>
            </span>
            <span v-else>{{ header.text }}</span>
          </th>
        </tr>
      </template>

      <template
        slot="items"
        slot-scope="props"
      >
        <tr
          :class="[
            { 'red lighten-4': props.item.isRemoved },
            rowColor(props.item),
            'clickable-row',
          ]"
        >
          <td
            v-for="column in visibleColumns"
            :key="column.name"
            class="pl-3 pr-0 py-1"
            @dblclick="
              $router
                .push(
                  rowTo
                    ? {
                      name: rowTo,
                      params: { id: props.item.id, isDraft: allowDelete },
                    }
                    : { path: `/${props.item.resource}/${props.item.id}` }
                )
                .catch((err) => {})
            "
          >
            <div
              v-if="isDate(column.name) || column.type === 'date'"
              class="cell-fixed-height cell-align-center"
            >
              {{
                props.item[column.name]
                  ? moment(props.item[column.name]).format("DD.MM.YY")
                  : ""
              }}
            </div>
            <div
              v-else-if="column.type === 'mark'"
              class="cell-fixed-height cell-align-center cell-justify-center"
            >
              <div
                v-if="!props.item[column.name]"
                class="handleByAdminMark"
              />
            </div>
            <div
              v-else-if="column.type === 'planFactDates'"
              class="cell-fixed-height cell-align-center"
            >
              <PlanFactDates
                :plan="
                  props.item[column.name] ? props.item[column.name].Plan : null
                "
                :fact="
                  props.item[column.name] ? props.item[column.name].Fact : null
                "
              />
            </div>
            <div
              v-else-if="column.type === 'dateRange'"
              class="cell-fixed-height cell-align-center"
            >
              {{
                props.item[column.name]
                  ? $options.filters.dateRange(props.item[column.name])
                  : ""
              }}
            </div>
            <div
              v-else-if="column.type === 'decimal' || column.type === 'int'"
              class="cell-fixed-height cell-align-center"
            >
              {{
                props.item[column.name]
                  ? $options.filters.separateDigits(
                    props.item[column.name]
                      .toFixed(column.scale)
                      .replace(".", ",")
                  )
                  : "0,000"
              }}
            </div>
            <div
              v-else-if="column.name === 'phone'"
              class="cell-fixed-height cell-align-center"
            >
              {{
                props.item[column.name]
                  ? $options.filters.phoneInternal(props.item[column.name])
                  : ""
              }}
            </div>
            <a
              v-else-if="column.isEditLink || column.name === 'id'"
              @click="
                $router
                  .push({
                    path: `/${props.item.resource}/${props.item.id}`,
                  })
                  .catch((err) => {})
              "
            >{{ props.item[column.name] }}</a>
            <v-icon
              v-else-if="column.type === 'flag'"
              color="green"
            >
              {{ props.item[column.name] ? "done" : "" }}
            </v-icon>
            <div
              v-else-if="column.type === 'bool'"
              class="cell-fixed-height cell-align-center"
            >
              {{
                props.item[column.name]
                  ? column.trueTitle
                    ? column.trueTitle
                    : "Да"
                  : column.falseTitle
                    ? column.falseTitle
                    : "Нет"
              }}
            </div>
            <div
              v-else-if="column.type === 'label'"
              class="cell-fixed-height cell-align-center"
            >
              <LabelView
                :item="props.item[column.name]"
                :false-title="column.falseTitle"
                :true-title="column.trueTitle"
              />
            </div>
            <div v-else-if="column.type === 'labelEnums'">
              <LabelEnums :item="props.item[column.name]" />
            </div>
            <div
              v-else-if="column.type === 'text-array'"
              class="cell-fixed-height cell-align-center"
            >
              <TextCell
                v-if="showTooltip(props.item[column.name], column.maxLength)"
                :text="props.item[column.name].join('; ')"
                :max-length="column.maxLength"
              />
              <span
                v-else
                :style="getCropTextCell(column)"
              >
                {{
                  props.item[column.name] && props.item[column.name].length > 0
                    ? props.item[column.name].join("; ")
                    : ""
                }}
              </span>
            </div>
            <div
              v-else-if="column.type === 'text-array-phone'"
              class="cell-fixed-height cell-align-center"
            >
              {{
                props.item[column.name] && props.item[column.name].length > 0
                  ? mapPhones(props.item[column.name])
                  : ""
              }}
            </div>
            <div
              v-else-if="column.type === 'tooltip'"
              class="cell-fixed-height cell-align-center"
            >
              <v-tooltip
                v-if="props.item[column.name]"
                top
              >
                <template v-slot:activator="{ on }">
                  <span v-on="on">{{ props.item[column.name] }}</span>
                </template>
                <span>{{ column.tooltip }}</span>
              </v-tooltip>
              <span v-else>{{ props.item[column.name] }}</span>
            </div>
            <div v-else-if="column.type === 'object'">
              {{ props.item[column.name].Title }}
            </div>
            <div
              v-else-if="column.type === 'time'"
              class="cell-fixed-height cell-align-center"
            >
              {{
                props.item[column.name]
                  ? $options.filters.time(props.item[column.name])
                  : ""
              }}
            </div>

            <TextCell
              v-else-if="
                column.maxLength &&
                  props.item[column.name] &&
                  props.item[column.name].length >= column.maxLength
              "
              :text="props.item[column.name]"
              :max-length="column.maxLength"
            />
            <div
              v-else-if="column.type === 'icon'"
              class="cell-fixed-height cell-align-center"
            >
              <TableCell
                v-if="props.item[column.name]"
                :icon="column.icon"
                :tooltip="column.tooltip"
                :color="column.color"
              />
            </div>
            <div
              v-else
              class="cell-fixed-height cell-align-center"
            >
              <span :style="getCropTextCell(column)">
                {{ props.item[column.name] }}
              </span>
            </div>
          </td>
          <td class="text-xs-right pa-2">
            <v-icon
              @click="
                $router
                  .push(
                    rowTo
                      ? {
                        name: rowTo,
                        params: {
                          id: props.item.id,
                          isDraft: allowDelete,
                        },
                      }
                      : { path: `/${props.item.resource}/${props.item.id}` }
                  )
                  .catch((err) => {})
              "
            >
              edit
            </v-icon>
          </td>
        </tr>
      </template>
    </v-data-table>

    <v-flex class="text-xs-center">
      <v-pagination
        v-model="pagination.page"
        :length="pages"
        :total-visible="14"
        data-test="pagination"
      />
    </v-flex>
  </v-layout>
</template>

<script>
import DataFilter from "./DataFilter.vue";
import SwitchFilter from "./SwitchFilter.vue";
import FilterView from "./FilterView.vue";
import DataSort from "./DataSort.vue";
import DataColumnsSettings from "./ColumnsSettings.vue";
import LabelView from "../label/LabelView.vue";
import LabelEnums from "../label/LabelEnums.vue";
import TableCell from "../../components/TableCell.vue";
import TextCell from "../../views/routes-planning/components/TextCell.vue";
import * as dataListApi from "../../backend/dataListApi.js";
import { saveDataListState } from "../../helpers/dataListState.js";
import { createUrl } from "../../helpers/urlQueryHelper.js";
import { createFilterParams } from "../../helpers/filterParams.js";

export default {
  components: {
    DataFilter,
    SwitchFilter,
    FilterView,
    DataSort,
    DataColumnsSettings,
    LabelView,
    LabelEnums,
    TableCell,
    TextCell,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    resource: {
      type: String,
      required: true,
    },
    columns: {
      type: Array,
      required: true,
    },
    filter: {
      default: null,
      required: false,
    },
    disableAdding: {
      type: Boolean,
      default: false,
      required: false,
    },
    addRoute: {
      type: Object,
      default: null,
      required: false,
    },
    filterOptions: {
      type: Object,
      required: true,
    },
    rowTo: {
      type: String,
      default: "",
      required: false,
    },
    allowDelete: {
      type: Boolean,
      default: false,
      required: false,
    },
    store: {
      type: String,
      default: "",
      required: true,
    },
    rowColorFn: {
      type: Function,
      default() {},
      required: false,
    },
  },
  data() {
    return {
      totalItems: 0,
      sort: [],
      loadingExcel: false,
      disabledDownload: false,
    };
  },
  computed: {
    columnsSettings() {
      const columns = JSON.parse(JSON.stringify(this.columns));
      columns.forEach((c) => {
        c.visible = true;
        c.order = 0;
      });
      return columns;
    },
    pages() {
      if (
        !this.pagination ||
        this.pagination.rowsPerPage == null ||
        this.totalItems == null
      ) {
        return 0;
      }
      return Math.ceil(this.totalItems / this.pagination.rowsPerPage);
    },
    headers() {
      const headers = this.visibleColumns.map((c) => {
        return {
          text: c.title,
          sortable: false,
          align: "left",
          width: c.width,
          tooltip: c.tooltip,
        };
      });
      headers.push({ text: "", sortable: false });
      return headers;
    },
    visibleColumns() {
      const columns = JSON.parse(JSON.stringify(this.columns));
      columns.forEach((c) => {
        const column = this.storeColumns.find((sc) => sc.name === c.name);
        if (column) {
          c.visible = column.visible;
          c.order = column.order;
        }
        if (c.type === "icon") {
          c.order = 0;
        }
      });
      columns.sort((a, b) => {
        let aOrder = 0;
        if (Object.prototype.hasOwnProperty.call(a, "order")) {
          aOrder = a.order;
        } else {
          aOrder = this.columns.indexOf(a);
        }
        let bOrder = 0;
        if (Object.prototype.hasOwnProperty.call(b, "order")) {
          bOrder = b.order;
        } else {
          bOrder = this.columns.indexOf(b);
        }
        if (aOrder < bOrder) {
          return -1;
        }
        if (aOrder > bOrder) {
          return 1;
        }
        return 0;
      });
      return columns.filter((c) => c.visible);
    },
    items() {
      return this.$store.getters[`${this.store}/items`];
    },
    pagination: {
      get() {
        return this.$store.getters[`${this.store}/pagination`];
      },
      set(pagination) {
        this.$store.commit(`${this.store}/setPagination`, pagination);
      },
    },
    dataListFilter: {
      get() {
        return this.$store.getters[`${this.store}/filter`];
      },
      set(filter) {
        this.$store.commit(`${this.store}/setFilter`, filter);
      },
    },
    storeColumns: {
      get() {
        return this.$store.getters[`${this.store}/columns`];
      },
      set(settings) {
        this.$store.commit(`${this.store}/setColumns`, settings);
      },
    },
    filters: {
      get() {
        return this.$store.getters[`${this.store}/filters`];
      },
      set(filters) {
        this.$store.commit(`${this.store}/setFilters`, filters);
      },
    },
    loading: {
      get() {
        return this.$store.getters[`${this.store}/loading`];
      },
      set(loading) {
        this.$store.commit(`${this.store}/setLoading`, loading);
      },
    },
    dataListSort: {
      get() {
        return this.$store.getters[`${this.store}/sort`];
      },
      set(sort) {
        return this.$store.commit(`${this.store}/setSort`, sort);
      },
    },
    rowsCount() {
      if (this.filters.length !== 0) {
        return Math.floor((window.innerHeight - 231) / 50);
      } else {
        return Math.floor((window.innerHeight - 185) / 50);
      }
    },
    saveDataListState() {
      return saveDataListState;
    },
  },
  watch: {
    pagination: {
      handler() {
        localStorage.setItem("page", this.pagination.page);
        if (this.$store.getters[`${this.store}/allActionsRegistered`]) {
          this.getItems();
          this.$store.commit(`${this.store}/registerAction`, "pagging");
          if (
            this.pagination &&
            this.$router.currentRoute.query.page != this.pagination.page
          ) {
            this.saveDataListState(
              this.pagination.page,
              this.pagination.rowsPerPage,
              this.dataListFilter,
              this.dataListSort,
              this.storeColumns
            );
          }
        }
      },
      deep: true,
    },
    dataListFilter: {
      handler() {
        if (this.$store.getters[`${this.store}/allActionsRegistered`]) {
          this.$store.commit(`${this.store}/registerAction`, "filter");
          this.saveDataListState(
            1,
            this.pagination.rowsPerPage,
            this.dataListFilter,
            this.dataListSort,
            this.storeColumns
          );
          this.getItems();
        }
      },
      deep: true,
    },
    dataListSort: {
      handler() {
        this.saveDataListState(
          1,
          this.pagination.rowsPerPage,
          this.dataListFilter,
          this.dataListSort,
          this.storeColumns
        );
        this.getItems();
      },
      deep: true,
    },
    rowsCount: {
      handler() {
        this.$store.commit(`${this.store}/setPagination`, {
          descending: this.pagination.descending,
          page: this.pagination.page,
          rowsPerPage: this.rowsCount,
          sortBy: this.pagination.sortBy,
          totalItems: this.pagination.totalItems,
        });
        this.$store.commit(`${this.store}/registerAction`, "rowsCount");
      },
      deep: true,
    },
    storeColumns: {
      handler() {
        this.saveDataListState(
          this.pagination.page,
          this.pagination.rowsPerPage,
          this.dataListFilter,
          this.dataListSort,
          this.storeColumns
        );
        this.pagination = {
          descending: this.pagination.descending,
          page: this.pagination.page,
          rowsPerPage: this.rowsCount,
          sortBy: this.pagination.sortBy,
          totalItems: this.pagination.totalItems,
        };
        this.$store.commit(`${this.store}/registerAction`, "settings");
      },
      deep: true,
    },
  },
  mounted() {
    if (localStorage.getItem("page")) {
      try {
        const pagination = {
          descending: this.pagination.descending,
          page: JSON.parse(localStorage.getItem("page")),
          rowsPerPage: this.rowsCount,
          sortBy: this.pagination.sortBy,
          totalItems: this.pagination.totalItems,
        };
        this.$store.commit(`${this.store}/setPagination`, pagination);
      } catch (e) {
        localStorage.removeItem("pagination");
      }
    }
    if (localStorage.getItem("sort")) {
      try {
        this.$store.commit(
          `${this.store}/setSort`,
          JSON.parse(localStorage.getItem("sort"))
        );
      } catch (e) {
        localStorage.removeItem("sort");
      }
    }
    if (localStorage.getItem("filter")) {
      try {
        this.$store.commit(
          `${this.store}/setFilter`,
          JSON.parse(localStorage.getItem("filter"))
        );
      } catch (e) {
        localStorage.removeItem("filter");
      }
    }
    if (localStorage.getItem("quickFilter")) {
      try {
        this.$store.commit(
          `${this.store}/setQuickFilter`,
          JSON.parse(localStorage.getItem("quickFilter"))
        );
      } catch (e) {
        localStorage.removeItem("quickFilter");
      }
    }
    if (localStorage.getItem("settings")) {
      try {
        this.$store.commit(
          `${this.store}/setColumns`,
          JSON.parse(localStorage.getItem("settings"))
        );
      } catch (e) {
        localStorage.removeItem("settings");
      }
    }
  },
  created() {
    if (this.filterOptions && !this.dataListFilter) {
      const filter = {};
      for (const prop in this.filterOptions) {
        filter[prop] = { ...this.filterOptions[prop] };
        if (prop.type === "decimal" || prop.type === "int") {
          filter[prop].valueFrom = null;
          filter[prop].valueTo = null;
        } else {
          filter[prop].value = null;
        }
      }
      this.dataListFilter = filter;
      this.$store.commit(`${this.store}/setDefaultFilter`, filter);
    }

    if (!this.filters || this.filters.length === 0) {
      this.getSavedFiltersByPage(this.store);
    }

    this.disabledDownload = this.$route.params?.disabledDownload;
  },
  methods: {
    async getItemsFromApi() {
      this.$store.commit(`${this.store}/setLoading`, true);
      const url = createUrl(
        this.pagination.page,
        this.pagination.rowsPerPage,
        this.dataListFilter,
        this.dataListSort
      );
      const res = await dataListApi.getItemsFromApi(url);
      const items = res.data.Result.map((i) => {
        const item = { id: i.Id, isRemoved: i.IsRemoved };
        this.columns.forEach((c) => {
          if (c.dataPath) {
            const dataPathSegments = c.dataPath.split(".");
            let value = i;
            dataPathSegments.forEach((s) => (value ? (value = value[s]) : ""));
            item[c.name] = value;
          } else if (c.valueFn) {
            item[c.name] = c.valueFn(i);
          } else {
            if (Object.prototype.hasOwnProperty.call(i, c.name)) {
              item[c.name] = i[c.name];
            } else if (
              Object.prototype.hasOwnProperty.call(
                i,
                capitalizeFirstLetter(c.name)
              )
            ) {
              item[c.name] = i[capitalizeFirstLetter(c.name)];
            }
          }
        });
        item.resource = this.resource;
        if (this.resource === "shippings") {
          item.hasPhases = i.HasPhases;
          item.state = i.State;
        }
        if (this.resource === "goods-details") {
          item.ShippingState = i.Shipping.State;
        }
        return item;
      });
      this.$store.commit(`${this.store}/setLoading`, false);
      return { items, total: res.data.TotalItems };

      function capitalizeFirstLetter(src) {
        return src.charAt(0).toUpperCase() + src.slice(1);
      }
    },
    getItems() {
      return new Promise((resolve, reject) => {
        this.getItemsFromApi()
          .then((data) => {
            this.$store.commit(`${this.store}/setItems`, data.items);
            this.totalItems = data.total;
            resolve();
          })
          .catch((err) => {
            reject(err);
          });
      });
    },
    isDate(columnName) {
      var reg = /date+$/i;
      return reg.test(columnName);
    },
    async downloadOutputForm() {
      this.loadingExcel = true;
      const filterParams = createFilterParams(this.dataListFilter);
      const res = await dataListApi.downloadOutputForm(
        this.resource,
        filterParams
      );
      this.downloadAsExcel(res.data);
      this.loadingExcel = false;
    },
    downloadAsExcel(fileData) {
      const blob = new Blob([fileData], { type: "application/xlsx" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = this.title + ".xlsx";
      link.click();
    },
    async getSavedFiltersByPage(store) {
      this.filters = await dataListApi.getSavedFiltersByPage(store);
      this.filtersLoading = false;
      this.$store.commit(`${this.store}/registerAction`, "filtersLoading");
    },
    mapPhones(phones) {
      const mappedPhones = [];
      phones.forEach((phone) => {
        mappedPhones.push(this.$options.filters.phoneInternal(phone));
      });
      return mappedPhones.join("; ");
    },
    rowColor(item) {
      return this.rowColorFn ? this.rowColorFn(item) : "table-striped";
    },
    getCropTextCell(column) {
      return `min-width: ${column.width ? `${column.width}px` : "auto"};
      width: ${column.width ? `${column.width}px` : "auto"};
      white-space: ${column.width && !column.whiteSpace ? "nowrap" : "normal"};
      ${column.width ? "text-overflow: ellipsis" : ""};
      overflow:  ${column.width ? "hidden" : "visible"}`;
    },
    showTooltip(item, maxLength) {
      if (!item) {
        return;
      }
      if (item.length && item[0].length > maxLength) {
        return true;
      }
      if (item.length > 1) {
        return true;
      }
    },
  },
};
</script>

<style scoped>
.cell-fixed-height {
  max-height: 50px;
  height: 50px;
  overflow: hidden;
}
.cell-align-center {
  display: flex;
  align-items: center;
}
.scrollable-block {
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
}
.cell-justify-center {
  justify-content: center;
}
.handleByAdminMark {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: red;
}
</style>
